import { useCallback, useEffect, useMemo, useState } from "react";
import {
  playButtonSound,
  playCompleteSound,
  playCorrectSound,
  playWrongSound,
} from "../../constants";
import { useGame } from "../../context";
import { MathZooAPI } from "../../services/api";
import "./SchulteGridGame.css";

// 動物配置
const ANIMALS = [
  { emoji: "🐵", name: "小猴子" },
  { emoji: "🦊", name: "小狐狸" },
  { emoji: "🐰", name: "小兔子" },
  { emoji: "🦁", name: "小獅子" },
  { emoji: "🐼", name: "小熊貓" },
];

// 遊戲模式配置
const GAME_MODES = {
  sequential: {
    id: "sequential",
    name: "順序排列",
    icon: "🔢",
    description: "按數字順序點擊所有格子",
    subModes: [
      {
        id: "forward",
        name: "順數 1→N",
        questionText: "順數排列",
        getSequence: (n) => Array.from({ length: n }, (_, i) => i + 1),
      },
      {
        id: "backward",
        name: "倒數 N→1",
        questionText: "倒數排列",
        getSequence: (n) => Array.from({ length: n }, (_, i) => n - i),
      },
      {
        id: "odd",
        name: "奇數序列",
        questionText: "奇數排列",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 2 === 1),
      },
      {
        id: "even",
        name: "偶數序列",
        questionText: "偶數排列",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 2 === 0),
      },
    ],
  },
  classify: {
    id: "classify",
    name: "奇偶分類",
    icon: "🎯",
    description: "按規則點擊奇數或偶數",
    subModes: [
      {
        id: "oddOnly",
        name: "只點奇數",
        questionText: "奇數分類",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 2 === 1),
      },
      {
        id: "evenOnly",
        name: "只點偶數",
        questionText: "偶數分類",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 2 === 0),
      },
      {
        id: "alternate",
        name: "奇偶交替",
        questionText: "奇偶交替",
        getSequence: (n) => {
          const result = [];
          const max = Math.ceil(n / 2);
          for (let i = 1; i <= max; i++) {
            if (2 * i - 1 <= n) result.push(2 * i - 1);
            if (2 * i <= n) result.push(2 * i);
          }
          return result;
        },
      },
    ],
  },
  multiples: {
    id: "multiples",
    name: "倍數挑戰",
    icon: "✖️",
    description: "找出特定數字的倍數",
    subModes: [
      {
        id: "mult2",
        name: "2的倍數",
        questionText: "2的倍數",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 2 === 0),
      },
      {
        id: "mult3",
        name: "3的倍數",
        questionText: "3的倍數",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 3 === 0),
      },
      {
        id: "mult5",
        name: "5的倍數",
        questionText: "5的倍數",
        getSequence: (n) =>
          Array.from({ length: n }, (_, i) => i + 1).filter((x) => x % 5 === 0),
      },
    ],
  },
};

// 難度配置
const DIFFICULTY_LEVELS = [
  { level: 1, gridSize: 3, timeLimit: null, name: "初級 3×3" },
  { level: 2, gridSize: 4, timeLimit: null, name: "中級 4×4" },
  { level: 3, gridSize: 5, timeLimit: 60, name: "高級 5×5" },
  { level: 4, gridSize: 6, timeLimit: 90, name: "專家 6×6" },
];

// 獲取完成狀態輔助函數
const getCompletionStatus = () => {
  const historyKey = "schulteGridHistory";
  const history = JSON.parse(localStorage.getItem(historyKey) || "[]");

  // 統計每個模式/子模式/難度的完成情況
  const completedCombos = new Set();
  const bestScores = {};

  history.forEach((record) => {
    if (!record.timeout && record.accuracy >= 70) {
      const key = `${record.mode}-${record.subMode}-${record.difficulty}`;
      completedCombos.add(key);

      // 記錄最高分
      if (!bestScores[key] || record.score > bestScores[key].score) {
        bestScores[key] = {
          score: record.score,
          accuracy: record.accuracy,
          time: record.time,
          stars: record.accuracy === 100 ? 3 : record.accuracy >= 90 ? 2 : 1,
        };
      }
    }
  });

  return { completedCombos, bestScores };
};

// 生成隨機網格
const generateGrid = (size) => {
  const numbers = Array.from({ length: size * size }, (_, i) => i + 1);
  // Fisher-Yates 洗牌算法
  for (let i = numbers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
  }
  return numbers;
};

// 格子組件
const GridCell = ({ number, status, onClick, isTarget, showHint }) => {
  const getStatusClass = () => {
    switch (status) {
      case "correct":
        return "correct";
      case "wrong":
        return "wrong";
      case "completed":
        return "completed";
      default:
        return "";
    }
  };

  return (
    <div
      className={`schulte-cell ${getStatusClass()} ${
        isTarget && showHint ? "hint" : ""
      }`}
      onClick={() => onClick(number)}
    >
      {status !== "completed" && <span className="cell-number">{number}</span>}
      {status === "completed" && <span className="cell-check">✓</span>}
    </div>
  );
};

// 模式選擇組件
const ModeSelector = ({ onSelectMode }) => {
  const { completedCombos } = useMemo(() => getCompletionStatus(), []);

  // 計算每個模式的完成數
  const getModeCompletionCount = (mode) => {
    let completed = 0;
    let total = 0;
    mode.subModes.forEach((subMode) => {
      DIFFICULTY_LEVELS.forEach((diff) => {
        total++;
        const key = `${mode.name}-${subMode.name}-${diff.name}`;
        if (completedCombos.has(key)) {
          completed++;
        }
      });
    });
    return { completed, total };
  };

  return (
    <div className="mode-selector">
      <h2 className="mode-title">選擇遊戲模式</h2>
      <div className="mode-cards">
        {Object.values(GAME_MODES).map((mode) => {
          const { completed, total } = getModeCompletionCount(mode);
          const isFullyCompleted = completed === total;
          return (
            <div
              key={mode.id}
              className={`mode-card ${isFullyCompleted ? "completed" : ""}`}
              onClick={() => {
                playButtonSound();
                onSelectMode(mode);
              }}
            >
              {completed > 0 && (
                <span className="completion-badge">
                  {isFullyCompleted ? "✓" : `${completed}/${total}`}
                </span>
              )}
              <span className="mode-icon">{mode.icon}</span>
              <h3 className="mode-name">{mode.name}</h3>
              <p className="mode-desc">{mode.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 子模式選擇組件
const SubModeSelector = ({ mode, onSelectSubMode, onBack }) => {
  const { completedCombos } = useMemo(() => getCompletionStatus(), []);

  // 計算每個子模式的完成數
  const getSubModeCompletionCount = (subMode) => {
    let completed = 0;
    DIFFICULTY_LEVELS.forEach((diff) => {
      const key = `${mode.name}-${subMode.name}-${diff.name}`;
      if (completedCombos.has(key)) {
        completed++;
      }
    });
    return { completed, total: DIFFICULTY_LEVELS.length };
  };

  return (
    <div className="submode-selector">
      <button className="back-btn" onClick={onBack}>
        ← 返回
      </button>
      <h2 className="submode-title">
        {mode.icon} {mode.name}
      </h2>
      <div className="submode-cards">
        {mode.subModes.map((subMode) => {
          const { completed, total } = getSubModeCompletionCount(subMode);
          const isFullyCompleted = completed === total;
          return (
            <div
              key={subMode.id}
              className={`submode-card ${isFullyCompleted ? "completed" : ""}`}
              onClick={() => {
                playButtonSound();
                onSelectSubMode(subMode);
              }}
            >
              {completed > 0 && (
                <span className="completion-badge">
                  {isFullyCompleted ? "✓" : `${completed}/${total}`}
                </span>
              )}
              <span className="submode-name">{subMode.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 難度選擇組件
const DifficultySelector = ({ mode, subMode, onSelectDifficulty, onBack }) => {
  const { completedCombos, bestScores } = useMemo(
    () => getCompletionStatus(),
    [],
  );

  return (
    <div className="difficulty-selector">
      <button className="back-btn" onClick={onBack}>
        ← 返回
      </button>
      <h2 className="difficulty-title">選擇難度</h2>
      <div className="difficulty-cards">
        {DIFFICULTY_LEVELS.map((diff) => {
          const key = `${mode?.name}-${subMode?.name}-${diff.name}`;
          const isCompleted = completedCombos.has(key);
          const best = bestScores[key];
          return (
            <div
              key={diff.level}
              className={`difficulty-card ${isCompleted ? "completed" : ""}`}
              onClick={() => {
                playButtonSound();
                onSelectDifficulty(diff);
              }}
            >
              {isCompleted && <span className="completion-badge">✓</span>}
              <span className="diff-name">{diff.name}</span>
              <span className="diff-info">
                {diff.timeLimit ? `限時 ${diff.timeLimit}秒` : "無時間限制"}
              </span>
              {best && (
                <div className="best-score">
                  <span className="best-stars">{"⭐".repeat(best.stars)}</span>
                  <span className="best-score-value">{best.score}分</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 遊戲結果組件
const GameResult = ({
  result,
  difficulty,
  onReplay,
  onBack,
  onNextLevel,
  hasNextLevel,
  autoAdvanceCountdown,
}) => {
  const getStars = () => {
    if (result.accuracy === 100 && result.time < result.targetTime * 0.5)
      return 3;
    if (result.accuracy >= 90 && result.time < result.targetTime) return 2;
    if (result.accuracy >= 70) return 1;
    return 0;
  };

  const stars = getStars();

  return (
    <div className="game-result">
      <div className="result-card">
        <h2 className="result-title">
          {stars >= 2
            ? "🎉 太棒了！"
            : stars >= 1
            ? "👍 不錯！"
            : "💪 繼續加油！"}
        </h2>

        <div className="result-stars">
          {[1, 2, 3].map((s) => (
            <span key={s} className={`star ${s <= stars ? "earned" : ""}`}>
              ⭐
            </span>
          ))}
        </div>

        {/* 等級顯示 */}
        {difficulty && (
          <div className="result-difficulty">
            <span className="difficulty-badge">📊 {difficulty.name}</span>
          </div>
        )}

        <div className="result-stats">
          <div className="stat-item">
            <span className="stat-label">答對題數</span>
            <span className="stat-value">{result.correctCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">用時</span>
            <span className="stat-value">{result.time.toFixed(1)}秒</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">準確率</span>
            <span className="stat-value">{result.accuracy.toFixed(0)}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">得分</span>
            <span className="stat-value">{result.score}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">最大連擊</span>
            <span className="stat-value">{result.maxCombo}</span>
          </div>
        </div>

        {/* 自動進入下一關倒數 - 點擊立即跳轉 */}
        {autoAdvanceCountdown !== null && autoAdvanceCountdown > 0 && (
          <div
            className="auto-advance-notice clickable"
            onClick={() => {
              playButtonSound();
              onNextLevel();
            }}
          >
            <span>
              ⏳ {autoAdvanceCountdown} 秒後自動進入下一關（點擊立即開始）
            </span>
          </div>
        )}

        <div className="result-actions">
          <button
            className="btn-replay"
            onClick={() => {
              playButtonSound();
              onReplay();
            }}
          >
            🔄 再玩一次
          </button>
          <button
            className="btn-back"
            onClick={() => {
              playButtonSound();
              onBack();
            }}
          >
            🏠 返回選單
          </button>
        </div>
      </div>
    </div>
  );
};

// 主遊戲組件
const SchulteGridGame = ({ onBack }) => {
  const { state } = useGame();

  // 遊戲階段：menu, mode, submode, difficulty, playing, result
  const [gamePhase, setGamePhase] = useState("menu");
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedSubMode, setSelectedSubMode] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);

  // 自動進入下一關倒數
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState(null);

  // 遊戲狀態
  const [grid, setGrid] = useState([]);
  const [targetSequence, setTargetSequence] = useState([]);
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
  const [cellStatuses, setCellStatuses] = useState({});
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [gameResult, setGameResult] = useState(null);
  const [showHint, setShowHint] = useState(false);

  // 隨機動物
  const animal = useMemo(
    () => ANIMALS[Math.floor(Math.random() * ANIMALS.length)],
    [],
  );

  // 等級提升音效
  useEffect(() => {
    if (state.showLevelUp) {
      playCompleteSound();
    }
  }, [state.showLevelUp]);

  // 計時器
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer((t) => {
          const newTime = t + 0.1;
          // 檢查時間限制
          if (
            selectedDifficulty?.timeLimit &&
            newTime >= selectedDifficulty.timeLimit
          ) {
            endGame(true);
          }
          return newTime;
        });
      }, 100);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerRunning, selectedDifficulty]);

  // 開始遊戲
  const startGame = useCallback(
    (difficulty = selectedDifficulty, subMode = selectedSubMode) => {
      if (!difficulty || !subMode) return;
      const size = difficulty.gridSize;
      const totalNumbers = size * size;
      const newGrid = generateGrid(size);
      const sequence = subMode.getSequence(totalNumbers);

      setGrid(newGrid);
      setTargetSequence(sequence);
      setCurrentTargetIndex(0);
      setCellStatuses({});
      setTimer(0);
      setIsTimerRunning(true);
      setCorrectCount(0);
      setWrongCount(0);
      setCombo(0);
      setMaxCombo(0);
      setGameResult(null);
      setGamePhase("playing");
    },
    [selectedDifficulty, selectedSubMode],
  );

  // 保存遊戲歷史到 localStorage 和 MySQL
  const savePlayHistory = useCallback(
    async (result, difficulty, mode, subMode) => {
      // 保存到 localStorage
      const historyKey = "schulteGridHistory";
      const existingHistory = JSON.parse(
        localStorage.getItem(historyKey) || "[]",
      );

      const newRecord = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        mode: mode?.name || "",
        subMode: subMode?.name || "",
        difficulty: difficulty?.name || "",
        gridSize: difficulty?.gridSize || 0,
        time: result.time,
        accuracy: result.accuracy,
        score: result.score,
        correctCount: result.correctCount,
        wrongCount: result.wrongCount,
        maxCombo: result.maxCombo,
        timeout: result.timeout,
      };

      const updatedHistory = [newRecord, ...existingHistory].slice(0, 100); // 保留最近100條
      localStorage.setItem(historyKey, JSON.stringify(updatedHistory));

      // 保存到 MySQL (如果用戶已登入)
      if (state.user?.id) {
        try {
          await MathZooAPI.recordGame(state.user.id, {
            gameType: "schulteGrid",
            question: {
              mode: mode?.id || "",
              subMode: subMode?.id || "",
              difficulty: difficulty?.level || 0,
              gridSize: difficulty?.gridSize || 0,
            },
            correct: !result.timeout && result.accuracy >= 70,
            usedHint: false,
            scoreGained: result.score,
            starsGained:
              result.accuracy === 100
                ? 3
                : result.accuracy >= 90
                ? 2
                : result.accuracy >= 70
                ? 1
                : 0,
            timeSpent: Math.round(result.time * 1000), // 轉換為毫秒
          });
        } catch (error) {
          console.error("Failed to save game history to server:", error);
        }
      }
    },
    [state.user],
  );

  // 結束遊戲
  const endGame = useCallback(
    (timeout = false) => {
      setIsTimerRunning(false);

      const totalTargets = targetSequence.length;
      const accuracy =
        totalTargets > 0 ? (correctCount / totalTargets) * 100 : 0;
      const baseScore = Math.max(0, 1000 - timer * 10);
      const accuracyBonus = baseScore * (accuracy / 100);
      const comboBonus = maxCombo * 10;
      const finalScore = Math.round(accuracyBonus + comboBonus);

      const result = {
        time: timer,
        accuracy,
        score: finalScore,
        correctCount,
        wrongCount,
        maxCombo,
        targetTime: selectedDifficulty?.timeLimit || 60,
        timeout,
      };

      // 保存遊戲歷史
      savePlayHistory(
        result,
        selectedDifficulty,
        selectedMode,
        selectedSubMode,
      );

      setGameResult(result);
      setGamePhase("result");

      // 檢查是否有下一關，如果有則開始倒數
      const currentIndex = DIFFICULTY_LEVELS.findIndex(
        (d) => d.level === selectedDifficulty?.level,
      );
      if (currentIndex < DIFFICULTY_LEVELS.length - 1 && !timeout) {
        setAutoAdvanceCountdown(10);
      } else {
        setAutoAdvanceCountdown(null);
      }
    },
    [
      timer,
      correctCount,
      wrongCount,
      maxCombo,
      targetSequence,
      selectedDifficulty,
      selectedMode,
      selectedSubMode,
      savePlayHistory,
    ],
  );

  // 處理格子點擊
  const handleCellClick = useCallback(
    (number) => {
      if (gamePhase !== "playing") return;

      const currentTarget = targetSequence[currentTargetIndex];

      if (number === currentTarget) {
        // 正確
        playCorrectSound();
        setCellStatuses((prev) => ({ ...prev, [number]: "completed" }));
        setCorrectCount((c) => c + 1);
        setCombo((c) => {
          const newCombo = c + 1;
          setMaxCombo((m) => Math.max(m, newCombo));
          return newCombo;
        });

        // 檢查是否完成
        if (currentTargetIndex + 1 >= targetSequence.length) {
          playCompleteSound();
          endGame();
        } else {
          setCurrentTargetIndex((i) => i + 1);
        }
      } else {
        // 錯誤
        playWrongSound();
        setCellStatuses((prev) => ({ ...prev, [number]: "wrong" }));
        setWrongCount((w) => w + 1);
        setCombo(0);
        setTimer((t) => t + 2); // 錯誤懲罰

        // 短暫顯示錯誤狀態後恢復
        setTimeout(() => {
          setCellStatuses((prev) => {
            const newStatuses = { ...prev };
            if (newStatuses[number] === "wrong") {
              delete newStatuses[number];
            }
            return newStatuses;
          });
        }, 500);
      }
    },
    [gamePhase, targetSequence, currentTargetIndex, endGame],
  );

  // 返回主選單
  const handleBackToMenu = () => {
    playButtonSound();
    setGamePhase("menu");
    setSelectedMode(null);
    setSelectedSubMode(null);
    setSelectedDifficulty(null);
    setIsTimerRunning(false);
  };

  // 返回遊戲選擇
  const handleBackToGameSelector = () => {
    playButtonSound();
    if (onBack) {
      onBack();
    }
  };

  // 自動進入下一關倒數
  useEffect(() => {
    if (autoAdvanceCountdown === null || autoAdvanceCountdown < 0) return;

    if (autoAdvanceCountdown === 0) {
      // 倒數結束，自動進入下一關
      handleNextLevel();
      setAutoAdvanceCountdown(null);
      return;
    }

    const timer = setTimeout(() => {
      setAutoAdvanceCountdown((c) => c - 1);
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvanceCountdown]);

  // 獲取下一個難度
  const getNextDifficulty = () => {
    if (!selectedDifficulty) return null;
    const currentIndex = DIFFICULTY_LEVELS.findIndex(
      (d) => d.level === selectedDifficulty.level,
    );
    if (currentIndex < DIFFICULTY_LEVELS.length - 1) {
      return DIFFICULTY_LEVELS[currentIndex + 1];
    }
    return null;
  };

  // 進入下一關
  const handleNextLevel = () => {
    const nextDiff = getNextDifficulty();
    if (nextDiff) {
      setSelectedDifficulty(nextDiff);
      startGame(nextDiff, selectedSubMode);
    }
  };

  // 渲染遊戲網格
  const renderGrid = () => {
    const size = selectedDifficulty.gridSize;
    const currentTarget = targetSequence[currentTargetIndex];

    return (
      <div
        className="schulte-grid"
        style={{
          gridTemplateColumns: `repeat(${size}, 1fr)`,
          gridTemplateRows: `repeat(${size}, 1fr)`,
        }}
      >
        {grid.map((number, index) => (
          <GridCell
            key={index}
            number={number}
            status={cellStatuses[number]}
            onClick={handleCellClick}
            isTarget={number === currentTarget}
            showHint={showHint}
          />
        ))}
      </div>
    );
  };

  // 渲染遊戲界面
  const renderGameplay = () => {
    const currentTarget = targetSequence[currentTargetIndex];
    const progress = (currentTargetIndex / targetSequence.length) * 100;
    const remainingTime = selectedDifficulty?.timeLimit
      ? Math.max(0, selectedDifficulty.timeLimit - timer)
      : null;

    return (
      <div className="gameplay-container">
        {/* 頂部信息欄 */}
        <div className="game-header">
          <button className="back-btn" onClick={handleBackToMenu}>
            ✕
          </button>
          <div className="game-info">
            <span className="info-item">⏱️ {timer.toFixed(1)}s</span>
            {remainingTime !== null && (
              <span
                className={`info-item ${remainingTime < 10 ? "warning" : ""}`}
              >
                ⏳ {remainingTime.toFixed(0)}s
              </span>
            )}
            <span className="info-item">🎯 {combo}連擊</span>
          </div>
        </div>

        {/* 任務提示 */}
        <div className="question-display">
          {selectedSubMode?.questionText || selectedSubMode?.name}
        </div>

        {/* 動物提示 */}
        <div className="animal-prompt">
          <span className="animal-emoji">{animal.emoji}</span>
          <div className="prompt-bubble">
            <span>
              下個數字是{" "}
              <strong className="target-number">{currentTarget}</strong>
            </span>
          </div>
        </div>

        {/* 進度條 */}
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          ></div>
          <span className="progress-text">
            {currentTargetIndex} / {targetSequence.length}
          </span>
        </div>

        {/* 遊戲網格 */}
        {renderGrid()}

        {/* 提示按鈕 */}
        <button
          className={`hint-btn ${showHint ? "active" : ""}`}
          onClick={() => {
            playButtonSound();
            setShowHint(!showHint);
          }}
        >
          💡 提示
        </button>
      </div>
    );
  };

  return (
    <div className="schulte-grid-game">
      {/* 遊戲頭部 */}
      {gamePhase === "menu" && (
        <div className="game-header main-header">
          <button className="back-btn" onClick={handleBackToGameSelector}>
            ← 返回
          </button>
          <h1 className="game-title">
            <span className="title-icon">🧠</span>
            舒爾特方格探險
          </h1>
          <div className="header-spacer"></div>
        </div>
      )}

      {/* 主內容區 */}
      <div className="game-main">
        {gamePhase === "menu" && (
          <ModeSelector
            onSelectMode={(mode) => {
              setSelectedMode(mode);
              setGamePhase("submode");
            }}
          />
        )}

        {gamePhase === "submode" && selectedMode && (
          <SubModeSelector
            mode={selectedMode}
            onSelectSubMode={(subMode) => {
              setSelectedSubMode(subMode);
              setGamePhase("difficulty");
            }}
            onBack={() => setGamePhase("menu")}
          />
        )}

        {gamePhase === "difficulty" && (
          <DifficultySelector
            mode={selectedMode}
            subMode={selectedSubMode}
            onSelectDifficulty={(diff) => {
              setSelectedDifficulty(diff);
              startGame(diff, selectedSubMode);
            }}
            onBack={() => setGamePhase("submode")}
          />
        )}

        {gamePhase === "playing" && renderGameplay()}

        {gamePhase === "result" && gameResult && (
          <GameResult
            result={gameResult}
            difficulty={selectedDifficulty}
            onReplay={() => {
              setAutoAdvanceCountdown(null);
              startGame(selectedDifficulty, selectedSubMode);
            }}
            onBack={() => {
              setAutoAdvanceCountdown(null);
              handleBackToMenu();
            }}
            onNextLevel={() => {
              setAutoAdvanceCountdown(null);
              handleNextLevel();
            }}
            hasNextLevel={getNextDifficulty() !== null}
            autoAdvanceCountdown={autoAdvanceCountdown}
          />
        )}
      </div>
    </div>
  );
};

export default SchulteGridGame;
